import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { getUserAttributes } from '@mb/lib';
import { CustomIcon } from 'components/CustomIcon';
import useEmblaCarousel from 'embla-carousel-react';
import * as Styled from './FullscreenGallery.styles';
import { useTranslateCollections } from '../../../Banners/i18n';
import {
  AnalyticsEventAction,
  AnalyticsEventName,
  sendAnalyticsEvent,
} from '../../helpers/analitycs';
import {
  showVisitorModal,
  openSaveToBoardModal,
} from '../../helpers/saveToBoard';
import { GalleryContext } from '../../hooks/useGalleryContext';
import { HeaderInspirationImages, Image } from '../../types';

interface FullscreenGalleryProps {
  images: HeaderInspirationImages;
  isActive: boolean;
  currentImageIndex?: number;
}

export const FullscreenGallery: FC<FullscreenGalleryProps> = ({
  images,
  currentImageIndex = 0,
  isActive,
}) => {
  const t = useTranslateCollections();
  const { setFullScreenActive, fullScreenActive } = useContext(GalleryContext);
  const [selectedIndex, setSelectedIndex] = useState(currentImageIndex);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel();
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
    skipSnaps: true,
  });
  const userData = getUserAttributes();

  const commonAnalyticsParams = {
    name: 'Collection inspiration image lightbox',
    experiment_name: null,
    experiment_version: null,
    event_location: null,
    method: null,
    action: null,
    list_id: null,
    list_name: null,
    type: null,
  };

  const onThumbClick = useCallback(
    (image: Image, index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
      sendAnalyticsEvent({
        ...commonAnalyticsParams,
        event: AnalyticsEventName.SELECT_CONTENT,
        content_id: image.id,
        content_type: 'Curated collection inspiration image',
        name: image.name,
      });
    },
    [emblaMainApi, emblaThumbsApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();
    emblaMainApi.on('select', onSelect);
    emblaMainApi.on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  useEffect(() => {
    Promise.resolve().then(() => {
      if (emblaMainApi) {
        emblaMainApi.reInit();
        emblaMainApi.scrollTo(currentImageIndex, true);
      }
    });
  }, [currentImageIndex, emblaMainApi]);

  const handleClose = useCallback(() => {
    setFullScreenActive(false);
    sendAnalyticsEvent({
      ...commonAnalyticsParams,
      event: AnalyticsEventName.CLOSE,
    });
  }, [setFullScreenActive]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!fullScreenActive) return;

      if (e.key === 'Escape') {
        handleClose();
      }

      if (e.key === 'ArrowLeft') {
        if (emblaMainApi) emblaMainApi.scrollPrev();
        sendAnalyticsEvent({
          ...commonAnalyticsParams,
          event: AnalyticsEventName.CAROUSEL,
          action: AnalyticsEventAction.BACK,
        });
      }
      if (e.key === 'ArrowRight') {
        if (emblaMainApi) emblaMainApi.scrollNext();
        sendAnalyticsEvent({
          ...commonAnalyticsParams,
          event: AnalyticsEventName.CAROUSEL,
          action: AnalyticsEventAction.FORWARD,
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [emblaMainApi, fullScreenActive, handleClose]);

  const getCurrentImage = useCallback(
    (images: HeaderInspirationImages, index: number) => {
      return images[index].image[0];
    },
    [],
  );

  const handleSaveToBoard = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (userData.signin_token == null) {
        showVisitorModal();
        return;
      }

      const image = getCurrentImage(images, selectedIndex);

      openSaveToBoardModal({
        imageUrl: image.url,
        fileName: image.name,
        onSuccessAnalyticsEvents: [
          { ecommerce: null },
          {
            ...commonAnalyticsParams,
            event: AnalyticsEventName.ADD_TO_WISHLIST,
            content_id: image.id,
            content_type: 'Curated collection inspiration image',
            name: image.name,
            method: 'Single',
          },
        ],
      });
    },
    [images, selectedIndex, getCurrentImage],
  );

  return (
    <Styled.Wrapper
      isActive={isActive}
      id="inspiration-img-lightbox"
      title="Collection inspiration image lightbox"
    >
      <Styled.ActionButtons>
        <Styled.SaveToBoardButton
          size="large"
          variant="contained"
          color="primary"
          onClick={handleSaveToBoard}
        >
          {t('Add to Board')}
        </Styled.SaveToBoardButton>
        <Styled.CloseButton onClick={handleClose} size="medium">
          <span className="sr-only">Close</span>
          <CustomIcon name="close" size={1} />
        </Styled.CloseButton>
      </Styled.ActionButtons>
      <Styled.Viewport ref={emblaMainRef}>
        <Styled.Container>
          {images.map(({ image }, index) => (
            <Styled.Slide key={index}>
              <img src={image[0].url} alt="Inspiration image" />
            </Styled.Slide>
          ))}
        </Styled.Container>
      </Styled.Viewport>
      <Styled.Thumbs>
        <Styled.ThumbsViewport ref={emblaThumbsRef}>
          <Styled.ThumbsContainer>
            {images.map(({ image }, index) => (
              <Styled.Thumb
                key={index}
                onClick={() => onThumbClick(image[0], index)}
                selected={index === selectedIndex}
              >
                <img src={image[0].url} alt="Inspiration image" />
              </Styled.Thumb>
            ))}
          </Styled.ThumbsContainer>
        </Styled.ThumbsViewport>
      </Styled.Thumbs>
    </Styled.Wrapper>
  );
};
